import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BlurText from '../components/BlurText';
import { initScrollReveal } from '../App';
import './DokumentaciaPage.css';

const DokumentaciaPage = () => {
  useEffect(() => {
    // Initialize scroll reveal animations
    const timer = setTimeout(() => {
      initScrollReveal();
    }, 150);

    // Smooth scrolling pre anchor odkazy
    const handleAnchorClick = (e) => {
      const href = e.target.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
          
          // Update active states
          document.querySelectorAll('.docs-nav-links a, .docs-sidebar a').forEach(link => {
            link.classList.remove('active');
          });
          e.target.classList.add('active');
        }
      }
    };

    // Scroll spy pre navigáciu
    const handleScroll = () => {
      const sections = document.querySelectorAll('.docs-section');
      const navLinks = document.querySelectorAll('.docs-nav-links a, .docs-sidebar a');
      
      let current = '';
      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= (sectionTop - 300)) {
          current = section.getAttribute('id');
        }
      });

      navLinks.forEach(link => {
        link.classList.remove('active');
        if (link.getAttribute('href') === `#${current}`) {
          link.classList.add('active');
        }
      });
    };

    document.addEventListener('click', handleAnchorClick);
    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleAnchorClick);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleLoginClick = (e) => {
    e.preventDefault();
    console.log('Login clicked');
  };

  const handleLanguageToggle = () => {
    console.log('Language toggle clicked');
  };

  const handleCopyCode = (codeText, buttonElement) => {
    navigator.clipboard.writeText(codeText).then(() => {
      // Visual feedback
      const originalText = buttonElement.innerHTML;
      buttonElement.innerHTML = '✅ Skopírované!';
      buttonElement.style.background = 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0.1) 100%)';
      buttonElement.style.borderColor = 'rgba(34, 197, 94, 0.3)';
      
      setTimeout(() => {
        buttonElement.innerHTML = originalText;
        buttonElement.style.background = '';
        buttonElement.style.borderColor = '';
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
      const originalText = buttonElement.innerHTML;
      buttonElement.innerHTML = '❌ Chyba';
      setTimeout(() => {
        buttonElement.innerHTML = originalText;
      }, 2000);
    });
  };

  return (
    <div className="dokumentacia-page">
      <Header 
        onLoginClick={handleLoginClick}
        onLanguageToggle={handleLanguageToggle}
        cartCount={0}
      />
      
      {/* Dokumentácia Hero */}
      <section className="docs-hero">
        <div className="docs-hero-content">
          <BlurText
            text="Dokumentácia"
            delay={150}
            animateBy="words"
            direction="top"
            className="docs-hero-title"
          />
          <p className="docs-hero-subtitle scroll-reveal delay-200">
            Kompletný návod pre integráciu a používanie Dispetly AI dispečing systému
          </p>
        </div>
      </section>

      {/* Dokumentácia navigácia */}
      <nav className="docs-nav">
        <div className="docs-nav-content">
          <div className="docs-nav-links">
            <a href="#uvod" className="active">Úvod</a>
            <a href="#architektura">Architektúra</a>
            <a href="#user-flow">User Flow</a>
            <a href="#api">API Vrstvy</a>
            <a href="#dashboard">Dashboard</a>
            <a href="#security">Security</a>
            <a href="#roadmap">Roadmap</a>
          </div>
        </div>
      </nav>

      {/* Hlavný obsah */}
      <div className="docs-container">
        {/* Sidebar navigácia */}
        <aside className="docs-sidebar">
          <nav>
            <h3>Obsah</h3>
            <ul>
              <li><a href="#uvod" className="active">Úvod projektu</a></li>
              <li><a href="#ciel-platformy">Cieľ platformy</a></li>
              <li><a href="#architektura">Architektúra systému</a></li>
              <li><a href="#komponenty">Komponenty systému</a></li>
              <li><a href="#user-flow">Užívateľské flowy</a></li>
              <li><a href="#api">API vrstvy</a></li>
              <li><a href="#autentifikacia">Autentifikácia</a></li>
              <li><a href="#dashboard">Dashboard overview</a></li>
              <li><a href="#subscription">Subscription model</a></li>
              <li><a href="#security">Security & Access</a></li>
              <li><a href="#deployment">Deployment</a></li>
              <li><a href="#roadmap">Budúci vývoj</a></li>
            </ul>
          </nav>
        </aside>

        {/* Hlavný obsah dokumentácie */}
        <main className="docs-content">
          {/* Úvod */}
          <section className="docs-section scroll-reveal" id="uvod">
            <h2>Úvod projektu</h2>
            <p>
              Dispetly predstavuje kompletnú digitálnu infraštruktúru pre taxislužby, 
              ktorá využíva pokročilé technológie umelej inteligencie a hlasového rozpoznávania 
              na plnú automatizáciu dispečerskej činnosti.
            </p>
            
            <h3 id="ciel-platformy">Cieľ platformy</h3>
            <div className="info-box scroll-reveal delay-100">
              <div className="info-icon">🎯</div>
              <div>
                <strong>Dispetly je plne digitálna infraštruktúra pre taxislužby</strong>, ktorá pomocou umelej inteligencie 
                a hlasových technológií dokáže automaticky obsluhovať zákazníkov bez potreby ľudského dispečera. 
                Platforma je vhodná pre firmy pôsobiace lokálne (napr. Taxi Levice, Nitra, atď.), 
                ktoré chcú zefektívniť objednávky a znížiť náklady.
              </div>
            </div>
            
            <h3>Kľúčové funkcie systému</h3>
            <div className="features-grid">
              <div className="feature-card scroll-reveal delay-100">
                <div className="feature-icon">🎤</div>
                <h4>Rozpoznávanie hlasu zákazníka</h4>
                <p>Systém vie spracovať volanie od zákazníka a previesť reč na text</p>
              </div>
              <div className="feature-card scroll-reveal delay-200">
                <div className="feature-icon">🤖</div>
                <h4>Spracovanie požiadavky AI dispečerom</h4>
                <p>Na základe textu identifikuje odkiaľ–kam chce zákazník ísť</p>
              </div>
              <div className="feature-card scroll-reveal delay-300">
                <div className="feature-icon">📲</div>
                <h4>Odoslanie notifikácie vodičom</h4>
                <p>Vodiči dostanú ponuku jazdy cez aplikáciu alebo hovor</p>
              </div>
              <div className="feature-card scroll-reveal delay-400">
                <div className="feature-icon">✅</div>
                <h4>Potvrdenie alebo odmietnutie jazdy</h4>
                <p>Vodič reaguje a systém zaznamená rozhodnutie</p>
              </div>
              <div className="feature-card scroll-reveal delay-500">
                <div className="feature-icon">📊</div>
                <h4>Dashboard pre správu firmy</h4>
                <p>Manažér si vie vytvoriť profil, pridať taxíky, vodičov a sledovať štatistiky</p>
              </div>
              <div className="feature-card scroll-reveal delay-600">
                <div className="feature-icon">🔒</div>
                <h4>Bezpečné prihlásenie a registrácia</h4>
                <p>Používame moderné API riešenie na správu autentifikácie</p>
              </div>
              <div className="feature-card scroll-reveal delay-700">
                <div className="feature-icon">🎛️</div>
                <h4>Prispôsobenie AI dispečera</h4>
                <p>Možnosť prispôsobiť si hlas AI dispečera a odpovede</p>
              </div>
              <div className="feature-card scroll-reveal delay-800">
                <div className="feature-icon">💳</div>
                <h4>Platobná brána a subscription</h4>
                <p>Mesačný subscription model s integrovanou platobnou bránou</p>
              </div>
            </div>
          </section>

          {/* Architektúra systému */}
          <section className="docs-section scroll-reveal" id="architektura">
            <h2>Architektúra systému</h2>
            <p>
              Dispetly je navrhnutý ako modulárny systém s jasne definovanými komponentmi 
              a API vrstvami pre maximálnu škálovateľnosť a bezpečnosť.
            </p>

            <h3 id="komponenty">Hlavné komponenty systému</h3>
            <div className="steps-grid">
              <div className="step-card scroll-reveal delay-100">
                <div className="step-number">01</div>
                <h3>Frontend aplikácia</h3>
                <ul>
                  <li>Responzívna webová aplikácia s moderným dizajnom</li>
                  <li>Dark mode, gradienty, smooth animácie</li>
                  <li>Prihlásenie, registrácia, dashboard, cenník</li>
                  <li>UX optimalizované pre jednoduchý onboarding</li>
                </ul>
              </div>

              <div className="step-card scroll-reveal delay-200">
                <div className="step-number">02</div>
                <h3>Backend & API komunikácia</h3>
                <ul>
                  <li>Komunikácia s autentifikačným API</li>
                  <li>Práca s databázou (users, rides, história)</li>
                  <li>Externé API pre Speech-to-Text, Text-to-Speech</li>
                  <li>Notifikačný systém pre vodičov</li>
                </ul>

                <div className="code-block">
                  <div className="code-block-header">
                    <span className="code-language">API Architektúra</span>
                    <div className="copy-button" onClick={(e) => handleCopyCode(`microservices:
  - auth-service: User authentication & authorization
  - voice-service: Speech processing & AI responses  
  - notification-service: Driver notifications
  - ride-service: Ride management & matching
  - dashboard-service: Analytics & reporting
  - payment-service: Subscription management`, e.target)}>📋 Kopírovať</div>
                  </div>
                  <pre>{`microservices:
  - auth-service: User authentication & authorization
  - voice-service: Speech processing & AI responses  
  - notification-service: Driver notifications
  - ride-service: Ride management & matching
  - dashboard-service: Analytics & reporting
  - payment-service: Subscription management`}</pre>
                </div>
              </div>

              <div className="step-card scroll-reveal delay-300">
                <div className="step-number">03</div>
                <h3>Hlasový systém (AI Robot)</h3>
                <ul>
                  <li><strong>Input:</strong> Telefónny hovor → rozpoznanie reči</li>
                  <li><strong>Proces:</strong> Rozbor textu, logika spracovania</li>
                  <li><strong>Output:</strong> Výzva vodičovi cez hovor/app/web</li>
                </ul>

                <div className="success-box">
                  <div className="success-icon">🤖</div>
                  <div>
                    <strong>AI Workflow:</strong><br/>
                    Hovor → Speech-to-Text → NLP Processing → Location Extraction → Driver Matching → Notification
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* User Flow */}
          <section className="docs-section scroll-reveal" id="user-flow">
            <h2>Užívateľské flowy</h2>
            <p>
              Dispetly automatizuje celý proces od hovoru zákazníka až po potvrdenie jazdy vodičom 
              pomocou inteligentného workflow systému.
            </p>
            
            <h3>Customer → Robot → Driver → System Flow</h3>
            <div className="steps-grid">
              <div className="step-card scroll-reveal delay-100">
                <div className="step-number">01</div>
                <h3>Zákazník volá</h3>
                <ul>
                  <li>Zákazník zavolá na číslo taxislužby</li>
                  <li>AI robot prijme hovor a privíta zákazníka</li>
                  <li>Aktivuje sa hlasové rozpoznávanie</li>
                  <li>Zákazník vysloví požiadavku prirodzene</li>
                </ul>
              </div>

              <div className="step-card scroll-reveal delay-200">
                <div className="step-number">02</div>
                <h3>AI spracuje požiadavku</h3>
                <ul>
                  <li>Speech-to-Text konvertuje hovor na text</li>
                  <li>NLP extrauje lokácie a čas</li>
                  <li>Systém validuje adresy</li>
                  <li>Vypočíta sa približná cena a čas</li>
                </ul>
              </div>

              <div className="step-card scroll-reveal delay-300">
                <div className="step-number">03</div>
                <h3>Notifikácia vodičov</h3>
                <ul>
                  <li>Systém vyberie dostupných vodičov</li>
                  <li>Odošle push notifikáciu do aplikácie</li>
                  <li>Alternatívne: automatický hovor vodičovi</li>
                  <li>Zobrazí detaily jazdy v dashboarde</li>
                </ul>
              </div>

              <div className="step-card scroll-reveal delay-400">
                <div className="step-number">04</div>
                <h3>Potvrdenie a dokončenie</h3>
                <ul>
                  <li>Vodič potvrdí alebo odmietne jazdu</li>
                  <li>Pri potvrdení: zákazník dostane SMS s detailmi</li>
                  <li>Pri odmietnutí: systém osloví ďalšieho vodiča</li>
                  <li>Všetko sa zaznamenáva do databázy</li>
                </ul>
              </div>
            </div>

            <div className="info-box scroll-reveal delay-500">
              <div className="info-icon">⚡</div>
              <div>
                <strong>Výkon systému:</strong> Celý proces od hovoru po priradenie vodiča trvá priemerne 15-30 sekúnd.
                Systém dokáže spracovať viacero hovorov súčasne bez degradácie výkonu.
              </div>
            </div>
          </section>

          {/* API Vrstvy */}
          <section className="docs-section scroll-reveal" id="api">
            <h2>API vrstvy</h2>
            <p>
              Dispetly využíva modulárnu API architektúru s jasne definovanými endpointmi 
              pre integrácie s externými systémami a internú komunikáciu medzi službami.
            </p>
            
            <div className="api-section scroll-reveal delay-100" id="autentifikacia">
              <h3>Autentifikácia a Authorization</h3>
              <p>
                Všetky API volania vyžadujú platný JWT token. Implementujeme OAuth 2.0 
                s refresh tokenmi pre bezpečnosť.
              </p>

              <div className="code-block">
                <div className="code-block-header">
                  <span className="code-language">HTTP Headers</span>
                  <div className="copy-button" onClick={(e) => handleCopyCode(`Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...
Content-Type: application/json
X-API-Version: v1
X-Client-ID: dispetly-dashboard`, e.target)}>📋 Kopírovať</div>
                </div>
                <pre>{`Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...
Content-Type: application/json
X-API-Version: v1
X-Client-ID: dispetly-dashboard`}</pre>
              </div>
            </div>

            <div className="api-section scroll-reveal delay-200">
              <h3>Hlavné API Endpointy</h3>

              <div className="api-card">
                <div className="api-card-header">
                  <span className="api-method">POST</span>
                  <h4>Spracovanie hlasovej požiadavky</h4>
                </div>
                <div className="api-endpoint">POST /api/v1/voice/process</div>
                <p>Endpoint pre spracovanie hlasovej požiadavky od zákazníka.</p>
                
                <div className="code-block">
                  <div className="code-block-header">
                    <span className="code-language">JSON Request</span>
                    <div className="copy-button" onClick={(e) => handleCopyCode(`{
  "audio_stream": "base64_encoded_audio",
  "phone_number": "+421900123456",
  "taxi_company_id": "tc_12345",
  "call_duration": 45,
  "timestamp": "2024-01-15T14:30:00Z"
}`, e.target)}>📋 Kopírovať</div>
                  </div>
                  <pre>{`{
  "audio_stream": "base64_encoded_audio",
  "phone_number": "+421900123456",
  "taxi_company_id": "tc_12345",
  "call_duration": 45,
  "timestamp": "2024-01-15T14:30:00Z"
}`}</pre>
                </div>
              </div>

              <div className="api-card">
                <div className="api-card-header">
                  <span className="api-method">POST</span>
                  <h4>Notifikácia vodiča</h4>
                </div>
                <div className="api-endpoint">POST /api/v1/drivers/notify</div>
                <p>Systém pre odoslanie požiadavky na jazdu dostupným vodičom.</p>
                
                <div className="code-block">
                  <div className="code-block-header">
                    <span className="code-language">JSON Response</span>
                    <div className="copy-button" onClick={(e) => handleCopyCode(`{
  "ride_id": "rd_67890",
  "pickup_location": "Hlavná 15, Bratislava",
  "destination": "Letisko M.R. Štefánika",
  "estimated_price": 25.50,
  "estimated_duration": 20,
  "customer_phone": "+421900123456",
  "priority": "normal",
  "special_requirements": null
}`, e.target)}>📋 Kopírovať</div>
                  </div>
                  <pre>{`{
  "ride_id": "rd_67890",
  "pickup_location": "Hlavná 15, Bratislava",
  "destination": "Letisko M.R. Štefánika",
  "estimated_price": 25.50,
  "estimated_duration": 20,
  "customer_phone": "+421900123456",
  "priority": "normal",
  "special_requirements": null
}`}</pre>
                </div>
              </div>
            </div>
          </section>

          {/* Dashboard Overview */}
          <section className="docs-section scroll-reveal" id="dashboard">
            <h2>Dashboard overview</h2>
            
            <p>
              Admin dashboard poskytuje kompletnú správu taxislužby s možnosťou 
              konfigurácie AI asistenta, správy vozidiel a sledovania výkonnosti.
            </p>

            <h3>Hlavné sekcie dashboardu</h3>
            <div className="features-grid">
              <div className="feature-card scroll-reveal delay-100">
                <div className="feature-icon">🏢</div>
                <h4>Profil firmy</h4>
                <p>Názov, mesto, IČO, telefón, konfigurácia základných údajov</p>
              </div>
              <div className="feature-card scroll-reveal delay-200">
                <div className="feature-icon">🚗</div>
                <h4>Správa vozidiel</h4>
                <p>Pridávanie a editácia taxíkov, SPZ, značka, model, kapacita</p>
              </div>
              <div className="feature-card scroll-reveal delay-300">
                <div className="feature-icon">👨‍💼</div>
                <h4>Správa vodičov</h4>
                <p>Registrácia vodičov, priradenie vozidiel, dostupnosť</p>
              </div>
              <div className="feature-card scroll-reveal delay-400">
                <div className="feature-icon">📊</div>
                <h4>Štatistiky a reporty</h4>
                <p>Analýza jázd, výkazníctvo, tracking výkonnosti</p>
              </div>
              <div className="feature-card scroll-reveal delay-500">
                <div className="feature-icon">🎛️</div>
                <h4>Nastavenie AI asistenta</h4>
                <p>Konfigurácia hlasu, fráz, spôsobu kontaktovania vodičov</p>
              </div>
              <div className="feature-card scroll-reveal delay-600">
                <div className="feature-icon">🔔</div>
                <h4>Notifikačné preferencie</h4>
                <p>Nastavenie spôsobu komunikácie s vodičmi (app/hovor/SMS)</p>
              </div>
            </div>

            <h3 id="subscription">Subscription handling</h3>
            <div className="api-card">
              <div className="api-card-header">
                <span className="api-method" data-type="subscription">SUBSCRIPTION</span>
                <h4>Platobný model</h4>
              </div>
              <p>Dispetly využíva subscription model s mesačným poplatkom za používanie platformy.</p>
              
              <div className="code-block">
                <div className="code-block-header">
                  <span className="code-language">Subscription Features</span>
                  <div className="copy-button" onClick={(e) => handleCopyCode(`features:
  - Integrovaná platobná brána (Stripe/PayPal)
  - Automatické obnovenie subscription
  - Notifikácia o expirácii (7 dní vopred)
  - Graceful degradation pri neplatení
  - Možnosť upgrade/downgrade plánu
  - Billing história a faktúry`, e.target)}>📋 Kopírovať</div>
                </div>
                <pre>{`features:
  - Integrovaná platobná brána (Stripe/PayPal)
  - Automatické obnovenie subscription
  - Notifikácia o expirácii (7 dní vopred)
  - Graceful degradation pri neplatení
  - Možnosť upgrade/downgrade plánu
  - Billing história a faktúry`}</pre>
              </div>
            </div>
          </section>

          {/* Security & Access Control */}
          <section className="docs-section scroll-reveal" id="security">
            <h2>Security & Access Control</h2>
            
            <p>
              Dispetly implementuje enterprise-grade bezpečnostné opatrenia 
              na ochranu citlivých dát taxislužieb a ich zákazníkov.
            </p>

            <div className="features-grid">
              <div className="feature-card scroll-reveal delay-100">
                <div className="feature-icon">🔐</div>
                <h4>OAuth 2.0 + JWT</h4>
                <p>Moderná autentifikácia s refresh tokenmi a expirovaním</p>
              </div>
              <div className="feature-card scroll-reveal delay-200">
                <div className="feature-icon">🛡️</div>
                <h4>HTTPS + SSL/TLS</h4>
                <p>Všetka komunikácia je šifrovaná end-to-end</p>
              </div>
              <div className="feature-card scroll-reveal delay-300">
                <div className="feature-icon">🔄</div>
                <h4>Rate Limiting</h4>
                <p>Ochrana proti DDoS útokom a spam volaniám</p>
              </div>
              <div className="feature-card scroll-reveal delay-400">
                <div className="feature-icon">📝</div>
                <h4>Audit Logging</h4>
                <p>Kompletný záznam všetkých akcií pre compliance</p>
              </div>
            </div>

            <h3 id="deployment">Deployment architektúra</h3>
            <div className="code-block">
              <div className="code-block-header">
                <span className="code-language">Infrastructure</span>
                <div className="copy-button" onClick={(e) => handleCopyCode(`deployment:
  infrastructure: AWS/Azure Cloud
  containers: Docker + Kubernetes
  database: PostgreSQL (production) + Redis (cache)
  monitoring: Prometheus + Grafana
  ci_cd: GitHub Actions
  cdn: CloudFlare
  backup: Automated daily backups
  uptime: 99.9% SLA guarantee`, e.target)}>📋 Kopírovať</div>
              </div>
              <pre>{`deployment:
  infrastructure: AWS/Azure Cloud
  containers: Docker + Kubernetes
  database: PostgreSQL (production) + Redis (cache)
  monitoring: Prometheus + Grafana
  ci_cd: GitHub Actions
  cdn: CloudFlare
  backup: Automated daily backups
  uptime: 99.9% SLA guarantee`}</pre>
            </div>
          </section>

          {/* Budúci vývoj */}
          <section className="docs-section scroll-reveal" id="roadmap">
            <h2>Budúci vývoj (Roadmap)</h2>
            
            <p>
              Plánované funkcionality a vylepšenia pre verziu 2.0 a ďalšie iterácie platformy.
            </p>

            <h3>Verzia 2.0 - Plánované funkcionality</h3>
            <div className="steps-grid">
              <div className="step-card scroll-reveal delay-100">
                <div className="step-number">Q2</div>
                <h3>Mobilná aplikácia pre vodičov</h3>
                <ul>
                  <li>Natívna iOS/Android aplikácia</li>
                  <li>Real-time GPS tracking</li>
                  <li>Push notifikácie</li>
                  <li>Offline mode pre základné funkcie</li>
                </ul>
              </div>

              <div className="step-card scroll-reveal delay-200">
                <div className="step-number">Q3</div>
                <h3>Advanced Analytics</h3>
                <ul>
                  <li>Machine learning pre predikciu dopytu</li>
                  <li>Optimalizácia cien na základe dopytu</li>
                  <li>Prediktívne rozmiestnenie vozidiel</li>
                  <li>Customer behavior analytics</li>
                </ul>
              </div>

              <div className="step-card scroll-reveal delay-300">
                <div className="step-number">Q4</div>
                <h3>Integrácie & API rozšírenia</h3>
                <ul>
                  <li>Integrácia s Google Maps/Waze</li>
                  <li>Platobné brány (card payments v taxi)</li>
                  <li>CRM systémy pre veľké firmy</li>
                  <li>White-label riešenie</li>
                </ul>
              </div>
            </div>

            <div className="success-box scroll-reveal delay-400">
              <div className="success-icon">🚀</div>
              <div>
                <strong>Vízia:</strong> Dispetly sa stane štandardom pre digitalizáciu taxislužieb na Slovensku 
                a rozšíri sa do ďalších krajín V4 regiónu do roku 2025.
              </div>
            </div>

            <h3>Kontakt pre technické otázky</h3>
            <div className="info-box scroll-reveal delay-500">
              <div className="info-icon">📧</div>
              <div>
                <strong>Technická podpora:</strong><br/>
                E-mail: dev@dispetly.sk<br/>
                Telegram: @azzarro<br/>
                GitHub: github.com/dispetly<br/>
                Dokumentácia: docs.dispetly.sk
              </div>
            </div>
          </section>
        </main>
      </div>
      
      <Footer />
    </div>
  );
};

export default DokumentaciaPage; 